import { FC, useContext, useRef, useState } from 'react'
import { ChatbotUIContext } from '@/context/context'
import { createAssistantCollections } from '@/db/assistant-collections'
import { createAssistantFiles } from '@/db/assistant-files'
import { createAssistantTools } from '@/db/assistant-tools'
import { createAssistant, deleteAssistant, updateAssistant } from '@/db/assistants'
import { isAssistantAccessReady } from '@/components/sidebar/items/assistants/assistant-access-field'
import { createChat } from '@/db/chats'
import { createCollectionFiles } from '@/db/collection-files'
import { createCollection } from '@/db/collections'
import { createFile } from '@/db/files'
import { createModel } from '@/db/models'
import { createPreset } from '@/db/presets'
import { createPrompt } from '@/db/prompts'
import { getAssistantImageFromStorage, uploadAssistantImage } from '@/db/storage/assistant-images'
import { createTool } from '@/db/tools'
import { Tables, TablesInsert } from '@/supabase/types'
import { ContentType } from '@/types'
import { toast } from 'sonner'
import { validateAttachmentUpload } from '@/lib/subscription'

import { convertBlobToBase64 } from '@/lib/blob-to-b64'
import { extractDocxRawText } from '@/lib/docx/extract-docx-raw-text'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'

interface SidebarCreateItemProps {
  isOpen: boolean
  isTyping: boolean
  onOpenChange: (isOpen: boolean) => void
  contentType: ContentType
  name?: string
  renderInputs: () => JSX.Element
  createState: any
  headerAction?: React.ReactNode
  isAIHelperOpen?: boolean
}

export const SidebarCreateItem: FC<SidebarCreateItemProps> = ({
  isOpen,
  onOpenChange,
  contentType,
  renderInputs,
  createState,
  isTyping,
  name,
  headerAction,
  isAIHelperOpen
}) => {
  const {
    selectedWorkspace,
    profile,
    setChats,
    setPresets,
    setPrompts,
    setFiles,
    setCollections,
    setAssistants,
    setAssistantImages,
    setTools,
    setPlatformTools,
    setModels
  } = useContext(ChatbotUIContext)

  const buttonRef = useRef<HTMLButtonElement>(null)

  const [creating, setCreating] = useState(false)

  const resolvedName = name || contentType

  const createFunctions = {
    chats: async (createState: TablesInsert<'chats'>) => createChat(createState),
    presets: createPreset,
    prompts: createPrompt,
    files: async (createState: { file: File } & TablesInsert<'files'>, workspaceId: string) => {
      if (!selectedWorkspace) return

      const { file, ...rest } = createState
      if (!profile) {
        throw new Error('Profile missing for upload')
      }

      const validation = validateAttachmentUpload(profile, [{ size: file.size }])
      if (!validation.ok) {
        toast.error(validation.message)
        throw new Error(validation.message)
      }

      // Handle DOCX files
      if (
        file.type.includes('vnd.openxmlformats-officedocument.wordprocessingml.document') ||
        file.type.includes('docx')
      ) {
        try {
          const arrayBuffer = await file.arrayBuffer()
          const extractedText = await extractDocxRawText(arrayBuffer)

          const createdFile = await createFile(
            file,
            rest,
            workspaceId,
            selectedWorkspace.embeddings_provider as 'openai' | 'local',
            extractedText
          )
          return createdFile
        } catch (error) {
          console.error('Error processing DOCX:', error)
          throw new Error('Failed to process DOCX file')
        }
      }

      // Handle all other file types
      const createdFile = await createFile(
        file,
        rest,
        workspaceId,
        selectedWorkspace.embeddings_provider as 'openai' | 'local'
      )
      return createdFile
    },
    collections: async (
      createState: {
        image: File
        collectionFiles: TablesInsert<'collection_files'>[]
      } & Tables<'collections'>,
      workspaceId: string
    ) => {
      const { collectionFiles, ...rest } = createState

      const createdCollection = await createCollection(rest, workspaceId)

      const finalCollectionFiles = collectionFiles.map((collectionFile) => ({
        ...collectionFile,
        collection_id: createdCollection.id
      }))

      await createCollectionFiles(finalCollectionFiles)

      return createdCollection
    },
    assistants: async (
      createState: {
        image: File
        files: Tables<'files'>[]
        collections: Tables<'collections'>[]
        tools: Tables<'tools'>[]
        attachmentDraft?: {
          spaceIds?: string[]
          skillIntegrationIds?: string[]
          virtualFileIds?: string[]
          mcpIntegrationIds?: string[]
          workspaceSkillIds?: string[]
          fileIds?: string[]
        }
        memberUserIds?: string[]
      } & Tables<'assistants'>,
      workspaceId: string
    ) => {
      const {
        image,
        files,
        collections,
        tools,
        attachmentDraft,
        memberUserIds,
        ...rest
      } = createState

      if (rest.sharing === 'shared' && !(memberUserIds || []).length) {
        toast.error('Add at least one person, or choose Only you.')
        throw new Error('Assistant people access requires at least one person')
      }

      const assistantData: TablesInsert<'assistants'> = {
        ...rest,
        // People list is written via access API after the row exists.
        sharing: rest.sharing === 'shared' ? 'private' : rest.sharing,
        image_path: ''
      }

      const createdAssistant = await createAssistant(assistantData, workspaceId)

      if (rest.sharing === 'shared') {
        const { authenticatedFetch } = await import(
          '@/lib/auth/authenticated-fetch'
        )
        let accessError: Error | null = null
        try {
          const accessRes = await authenticatedFetch(
            `/api/assistants/${createdAssistant.id}/access`,
            {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                sharing: 'shared',
                memberUserIds: memberUserIds || [],
                workspaceId
              })
            }
          )
          if (!accessRes.ok) {
            const accessData = await accessRes.json().catch(() => null)
            accessError = new Error(
              accessData?.error ||
                'Couldn’t save people access. Assistant was not kept.'
            )
          }
        } catch (error) {
          console.error('Failed to save Assistant people access:', error)
          accessError =
            error instanceof Error
              ? error
              : new Error('Couldn’t save people access. Assistant was not kept.')
        }
        if (accessError) {
          await deleteAssistant(createdAssistant.id).catch(() => undefined)
          throw accessError
        }
      }

      if (
        attachmentDraft &&
        (attachmentDraft.spaceIds?.length ||
          attachmentDraft.skillIntegrationIds?.length ||
          attachmentDraft.virtualFileIds?.length ||
          attachmentDraft.mcpIntegrationIds?.length ||
          attachmentDraft.workspaceSkillIds?.length ||
          attachmentDraft.fileIds?.length)
      ) {
        try {
          const { authenticatedFetch } = await import(
            '@/lib/auth/authenticated-fetch'
          )
          const attachRes = await authenticatedFetch(
            `/api/assistants/${createdAssistant.id}/attachments`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                spaceIds: attachmentDraft.spaceIds || [],
                skillIntegrationIds: attachmentDraft.skillIntegrationIds || [],
                virtualFileIds: attachmentDraft.virtualFileIds || [],
                mcpIntegrationIds: attachmentDraft.mcpIntegrationIds || [],
                workspaceSkillIds: attachmentDraft.workspaceSkillIds || [],
                fileIds: attachmentDraft.fileIds || []
              })
            }
          )
          const attachData = await attachRes.json().catch(() => null)
          const rejected = Array.isArray(attachData?.rejected)
            ? attachData.rejected
            : []
          if (!attachRes.ok) {
            toast.error(
              'Assistant created, but attachments could not be saved. Open the Assistant to retry.'
            )
          } else if (rejected.length) {
            toast.warning(
              `Assistant created. Couldn’t attach: ${rejected.join(', ')} — not in this workspace.`
            )
          }
        } catch (error) {
          console.error('Failed to attach Assistant skills/Spaces/vfs:', error)
          toast.error(
            'Assistant created, but attachments failed to save. Open the Assistant to retry.'
          )
        }
      }

      if (tools && tools.length > 0) {
        await createAssistantTools(
          tools.map((tool: Tables<'tools'>) => ({
            user_id: rest.user_id,
            assistant_id: createdAssistant.id,
            tool_id: tool.id
          })),
          tools
        )
      }

      if (files && files.length > 0) {
        await createAssistantFiles(
          files.map((file) => ({
            user_id: rest.user_id,
            assistant_id: createdAssistant.id,
            file_id: file.id
          }))
        )
      }

      if (collections && collections.length > 0) {
        await createAssistantCollections(
          collections.map((collection) => ({
            user_id: rest.user_id,
            assistant_id: createdAssistant.id,
            collection_id: collection.id
          }))
        )
      }

      let updatedAssistant = createdAssistant

      // Handle image upload - either from file upload or data URL (like AI-generated icons)
      let imageToUpload: File | null = null

      if (image) {
        // Direct file upload
        imageToUpload = image
      } else if (rest.image_path && rest.image_path.startsWith('data:')) {
        // Convert data URL to File object (for AI-generated icons)
        try {
          const response = await fetch(rest.image_path)
          const blob = await response.blob()
          imageToUpload = new File([blob], 'assistant-icon.png', { type: 'image/png' })
        } catch (error) {
          console.error('Failed to convert data URL to file:', error)
        }
      }

      if (imageToUpload) {
        const filePath = await uploadAssistantImage(
          {
            ...createdAssistant,
            tools: []
          },
          imageToUpload
        )

        updatedAssistant = await updateAssistant(createdAssistant.id, {
          image_path: filePath
        })

        const url = (await getAssistantImageFromStorage(filePath)) || ''

        if (url) {
          const response = await fetch(url)
          const blob = await response.blob()
          const base64 = await convertBlobToBase64(blob)

          setAssistantImages((prev) => [
            ...prev,
            {
              assistantId: updatedAssistant.id,
              path: filePath,
              base64,
              url
            }
          ])
        }
      }

      return updatedAssistant
    },
    tools: createTool,
    models: createModel
  }

  const stateUpdateFunctions = {
    chats: setChats,
    presets: setPresets,
    prompts: setPrompts,
    files: setFiles,
    collections: setCollections,
    assistants: setAssistants,
    tools: setTools,
    platformTools: setPlatformTools,
    models: setModels
  }

  const handleCreate = async () => {
    try {
      if (!selectedWorkspace) return
      if (isTyping) return // Prevent creation while typing

      // @ts-ignore
      const createFunction = createFunctions[contentType]
      // @ts-ignore
      const setStateFunction = stateUpdateFunctions[contentType]

      if (!createFunction || !setStateFunction) return

      setCreating(true)

      const newItem = await createFunction(createState, selectedWorkspace.id)

      setStateFunction((prevItems: any) => [...prevItems, newItem])

      onOpenChange(false)
      setCreating(false)
    } catch (error) {
      toast.error(`Error creating ${resolvedName.slice(0, -1)}. ${error}.`)
      setCreating(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isTyping && !isAIHelperOpen && e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      buttonRef.current?.click()
    }
  }

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        className="flex w-[calc(100dvw_-_env(safe-area-inset-left)_-_env(safe-area-inset-right))] max-w-full flex-col justify-between overflow-auto px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-[max(1rem,env(safe-area-inset-top))] sm:w-[450px] sm:px-6"
        side="left"
        onKeyDown={handleKeyDown}
      >
        <div className="grow overflow-auto">
          <SheetHeader className="w-full">
            <div className="flex w-full flex-row items-center justify-between text-left md:flex-row md:items-center">
              <SheetTitle className="w-full text-2xl font-bold">
                Create {resolvedName.charAt(0).toUpperCase() + resolvedName.slice(1, -1)}
              </SheetTitle>
              {headerAction && (
                <div className="h-26 w-26 ml-5 flex justify-end">{headerAction}</div>
              )}
            </div>
          </SheetHeader>

          <div className="mt-4 space-y-3">{renderInputs()}</div>
        </div>

        <SheetFooter className="mt-2 flex justify-between">
          <div className="flex grow justify-end space-x-2">
            <Button disabled={creating} variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>

            <Button
              loading={creating}
              ref={buttonRef}
              onClick={handleCreate}
              disabled={
                creating ||
                (contentType === 'assistants' &&
                  !isAssistantAccessReady(
                    createState?.sharing,
                    createState?.memberUserIds || []
                  ))
              }
            >
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
